import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CarerAccount } from '../types/index';
import { authService } from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  carer: CarerAccount | null;
  token: string | null;
  login: (phoneNumber: string, verificationCode: string) => Promise<void>;
  register: (phoneNumber: string, verificationCode: string, name: string, role: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshCarer: () => Promise<void>;
  loading: boolean;
  canManageUser: (userId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [carer, setCarer] = useState<CarerAccount | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 初始化认证状态
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const authState = authService.getAuthState();
        
        if (authState.isAuthenticated) {
          // 首先直接从localStorage恢复基本状态
          setIsAuthenticated(true);
          setCarer(authState.carer);
          setToken(authState.token);
          console.log('🔐 从本地存储恢复认证状态:', authState.carer?.name);
          
          // 然后在后台验证token是否仍然有效（不阻塞UI）
          authService.getCurrentCarer()
            .then(currentCarer => {
              if (currentCarer) {
                // 更新为最新的照护者信息
                setCarer(currentCarer);
                console.log('✅ Token验证成功，信息已更新:', currentCarer.name);
              } else {
                console.log('⚠️ Token验证失败，但保持本地状态');
              }
            })
            .catch(error => {
              console.warn('⚠️ 后台Token验证失败，但保持本地状态:', error);
              // 不清除认证状态，让用户继续使用
            });
        }
      } catch (error) {
        console.warn('⚠️ 初始化认证状态失败:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // 登录
  const login = async (phoneNumber: string, verificationCode: string) => {
    try {
      console.log('🔐 AuthContext.login() 开始调用authService.login...');
      const result = await authService.login({ phoneNumber, verificationCode });
      
      console.log('🔐 AuthService登录响应:', {
        hasToken: !!result.token,
        hasCarer: !!result.carer,
        carerName: result.carer?.name,
        carerRole: result.carer?.role
      });
      
      // 立即同步更新React状态
      setIsAuthenticated(true);
      setCarer(result.carer!);
      setToken(result.token!);
      
      console.log('✅ AuthContext状态更新完成:', {
        isAuthenticated: true,
        carerName: result.carer?.name,
        hasToken: !!result.token
      });
      
      // 验证authService的内部状态
      const authState = authService.getAuthState();
      console.log('📊 AuthService内部状态验证:', authState);
      
      console.log('✅ 登录成功:', result.carer?.name);
    } catch (error) {
      console.error('❌ 登录失败:', error);
      throw error;
    }
  };

  // 注册
  const register = async (phoneNumber: string, verificationCode: string, name: string, role: any) => {
    try {
      const result = await authService.register({ 
        phoneNumber, 
        verificationCode, 
        name, 
        role 
      });
      
      setIsAuthenticated(true);
      setCarer(result.carer!);
      setToken(result.token!);
      
      console.log('✅ 注册成功:', result.carer?.name);
    } catch (error) {
      console.error('❌ 注册失败:', error);
      throw error;
    }
  };

  // 登出
  const logout = async () => {
    try {
      await authService.logout();

      setIsAuthenticated(false);
      setCarer(null);
      setToken(null);

      // 清除用户选择状态，防止跨照护者数据污染
      localStorage.removeItem('selectedUserId');
      sessionStorage.removeItem('currentUserId');

      console.log('✅ 已登出，用户状态已清除');
    } catch (error) {
      console.error('❌ 登出失败:', error);
      throw error;
    }
  };

  // 刷新当前照护者信息
  const refreshCarer = async () => {
    try {
      // 首先检查本地存储的认证状态
      const authState = authService.getAuthState();
      if (authState.isAuthenticated && authState.carer) {
        setCarer(authState.carer);
        setIsAuthenticated(true);
        setToken(authState.token);
        console.log('✅ 从本地存储刷新照护者信息:', authState.carer.name);
      }
      
      // 然后尝试从服务器获取最新信息
      const updatedCarer = await authService.getCurrentCarer();
      if (updatedCarer) {
        setCarer(updatedCarer);
        console.log('✅ 从服务器刷新照护者信息:', updatedCarer.name);
      } else if (!authState.isAuthenticated) {
        // 如果本地也没有认证信息，清除状态
        setIsAuthenticated(false);
        setCarer(null);
        setToken(null);
      }
    } catch (error) {
      console.error('❌ 刷新照护者信息失败:', error);
      // 发生错误时，检查本地存储是否有备份信息
      const authState = authService.getAuthState();
      if (authState.isAuthenticated && authState.carer) {
        setCarer(authState.carer);
        setIsAuthenticated(true);
        setToken(authState.token);
        console.log('✅ 使用本地备份照护者信息:', authState.carer.name);
      }
    }
  };

  // 检查是否可以管理指定用户
  const canManageUser = (userId: string): boolean => {
    return authService.canManageUser(userId);
  };

  const value: AuthContextType = {
    isAuthenticated,
    carer,
    token,
    login,
    register,
    logout,
    refreshCarer,
    loading,
    canManageUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;